/**
 * File to load the front end asset for the block.
 * Contains functionality to load posts dynamically whenever user click on load more button.
 *
 * @package
 */

/**
 * WordPress dependencies
 */
import '@wordpress/util';
import { __ } from '@wordpress/i18n';
import { addQueryArgs, filterURLForDisplay } from '@wordpress/url';

const loadMore = {

	init() {
		document.addEventListener( 'DOMContentLoaded', this.setLoadmoreListener );
	},

	/**
	 * Function sets an event-listener for the infinite scroll.
	 *
	 * @return {void}
	 */
	setLoadmoreListener() {
		/**
		 * Constants to be used
		 */
		const containers = document.querySelectorAll( '.recent-posts__container-items' );
		const container = containers[ containers.length - 1 ]; // Select the last instance

		if ( ! container ) {
			return;
		}

		const dataAttr = JSON.parse( container?.getAttribute( 'data-query' ) );

		const { restUrl } = recentPostsConfig; /* global recentPostsConfig */

		const PATH = dataAttr.post_type === 'all'
			? `${ restUrl }ae-blog-features-2024/v1/all-posts`
			: `${ restUrl }wp/v2/${ dataAttr.post_type }`;

		const loadMoreButton = document.querySelector( '.load-more > a' );
		const archiveName = recentPostsConfig.archiveName;
		const archiveValue = recentPostsConfig.archiveValue;

		let currentPage = 1;

		// Set query.
		const DEFAULT_LIST_QUERY = {
			// eslint-disable-next-line camelcase
			per_page: dataAttr.no_of_posts,
			post_type: dataAttr.postType,
		};

		if ( '' !== archiveName && null !== archiveValue ) {
			DEFAULT_LIST_QUERY[ `${ archiveName }` ] = archiveValue;
		}

		if ( dataAttr.categories.length !== 0 ) {
			DEFAULT_LIST_QUERY.categories = dataAttr.categories;
		}

		if ( 'day' === archiveName && null !== archiveValue ) {
			DEFAULT_LIST_QUERY.day = archiveValue.day;
			DEFAULT_LIST_QUERY.monthnum = archiveValue.monthnum;
			DEFAULT_LIST_QUERY.year = archiveValue.year;
		}

		if ( 'month' === archiveName && null !== archiveValue ) {
			DEFAULT_LIST_QUERY.monthnum = archiveValue.monthnum;
			DEFAULT_LIST_QUERY.year = archiveValue.year;
		}

		if ( 'year' === archiveName && null !== archiveValue ) {
			DEFAULT_LIST_QUERY.year = archiveValue.year;
		}

		// Get block attributes.
		const dataBlockAttr = JSON.parse( container?.getAttribute( 'data-block-attributes' ) );

		// Destructure the block attributes.
		const { show_title: showTitle, show_published_date: showPublishedDate, show_author: showAuthor, show_categories: showCategories, show_excerpt: showExcerpt, show_see_more: showSeeMore } = dataBlockAttr;

		// When user click load more.
		if ( loadMoreButton ) {
			loadMoreButton.addEventListener( 'click', loadMorePosts );
			loadMoreButton.addEventListener( 'keypress', ( event ) => {
				if ( 13 === event.keyCode ) {
					loadMorePosts();
				}
			} );
		}
		/**
		 * Function for load more posts.
		 *
		 * @return {void}
		 */
		function loadMorePosts() {
			++currentPage;

			DEFAULT_LIST_QUERY.page = currentPage;

			const apiArgs = addQueryArgs( `${ PATH }`, DEFAULT_LIST_QUERY );
			const fetchUrl = apiArgs + '&_embed';
			const pageCount = parseInt( container.getAttribute( 'data-page-count' ) );

			if ( currentPage <= pageCount ) {
				toggleLoading( 'flex' );
				fetchPosts( fetchUrl );
			} else {
				toggleLoading( 'none' );
			}
		}

		/**
		 * Function for fetching results using REST API.
		 *
		 * @param {string} fetchUrl URL to fetch the posts.
		 *
		 * @return {void}
		 */
		const fetchPosts = ( fetchUrl ) => {
			const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
			const template = wp.template( 'load-more' );

			let dynamicHtml = '';
			const ratio = dataAttr.ratio;

			if ( loadMoreButton ) {
				loadMoreButton.textContent = __( 'Loading…', 'ae-blog-features-2024' );
			}

			try {
				fetch( fetchUrl, {
					credentials: 'same-origin',
					headers: {
						'Content-Type': 'application/json',
						'X-WP-Nonce': recentPostsConfig.nonce,
					},
				} )
					.then( ( response ) => {
						container.setAttribute( 'data-page-count', response.headers.get( 'x-wp-totalPages' ) );
						return response.json();
					} )
					.then( ( data ) => {
						data.map( ( recentPost ) => {
							recentPost.dateFormatted = new Date( recentPost.date ).toLocaleDateString( 'en-US', dateOptions );
							recentPost.ratio = ratio;
							recentPost.showTitle = showTitle;
							recentPost.showPublishedDate = showPublishedDate;
							recentPost.showAuthor = showAuthor;
							recentPost.showCategories = showCategories;
							recentPost.showExcerpt = showExcerpt;
							recentPost.showSeeMore = showSeeMore;
							recentPost.seeMoreText = __( 'SEE MORE', 'ae-blog-features-2024' );
							dynamicHtml += template( recentPost );
						},
						);
						const pageCount = parseInt( container.getAttribute( 'data-page-count' ) );

						if ( loadMoreButton ) {
							loadMoreButton.textContent = __( 'Load More', 'ae-blog-features-2024' );
						}

						const nodes = document.querySelectorAll( '.recent-posts__item' );
						const item = nodes[ nodes.length - 1 ];
						item.insertAdjacentHTML( 'afterend', dynamicHtml );

						if ( currentPage < pageCount ) {
							toggleLoading( 'flex' );
						} else {
							toggleLoading( 'none' );
						}
						container.style.visibility = 'visible';
					} );
			} catch ( error ) {
				console.log( 'There was an error: ', error );
			}
		};

		/**
		 * Function to toggle loading message.
		 *
		 * @param {string} display
		 */
		function toggleLoading( display ) {
			document.querySelector( '.load-more' ).style.display = display;
		}
	},

};

export default loadMore;
loadMore.init();
